import React from 'react';
import { Node, Edge, Position } from 'reactflow';

import {
  Callees,
  Callers,
  FlowByFunction,
  GetFunctionResponse,
  isEndpoint,
  isHttpEndpoint,
  isKafkaTopic,
  isSqsQueue,
} from '@hud/portal-dtos/src/lib/webapp-function.dto';
import { NodeData, EdgeData } from '../../Charts/FlowChart/types';
import {
  FLOWCHART_MIN_NODE_COMPACT,
  FLOWCHART_MIN_NODE_HEIGHT,
} from '../../Charts/FlowChart/CustomNodes';
import { NodeErrorConfig } from '@hud/portal-dtos/flow-chart.dtos';
import { EndpointNodeBody } from './EndpointNodeBody';
import { TopicNodeBody } from './TopicNodeBody';
import { getDisplayMethod } from '../../utils';
import { EndpointTooltipContent } from '../../EndpointTooltip/EndpointTooltip';
import { SQSQueueNodeBody } from './SqsNodeBody';
import { ModuleIcon } from '../../ThirdParty/ModuleIcon';
import { NodeTitleWithIconContainer } from './NodeTitleWithIconContainer';

const DEFAULT_NODE_CAP = 8;

const nodeDefaults = {
  sourcePosition: Position.Right,
  targetPosition: Position.Left,
  draggable: false,
  position: { x: 0, y: 0 },
};

export type TintColors = {
  error: {
    color: string;
    highlightColor: string;
  };
};

const DEFAULT_TINT_COLORS: TintColors = {
  error: {
    color: '#FF2E60a0',
    highlightColor: '#FF2E5F',
  },
};

export type NodeGenerationOptions = {
  alignCenterNodeToTop?: boolean;
  clickableEndpoints?: boolean;
  clickableTopics?: boolean;

  // Limit amount of visible notes (and show a 'show more' button)
  nodeCap?: number;

  // Default node width
  nodeWidth?: string;
};

const defaultOptions: NodeGenerationOptions = {
  alignCenterNodeToTop: true,
  clickableEndpoints: false,
  clickableTopics: false,
  nodeCap: DEFAULT_NODE_CAP,
};

export function generateNodesAndEdges(
  functionData: GetFunctionResponse,
  capCallers: boolean,
  capCallees: boolean,
  compact: boolean,
  loading?: boolean,

  showTinting?: NodeErrorConfig,
  options?: NodeGenerationOptions,
  tintColors: TintColors = DEFAULT_TINT_COLORS,
): [Node<NodeData>[], Edge<EdgeData>[]] {
  const NODE_HEIGHT = compact
    ? FLOWCHART_MIN_NODE_COMPACT
    : FLOWCHART_MIN_NODE_HEIGHT;

  options = { ...defaultOptions, ...options };

  let callers = functionData.callers;
  const callees = functionData.callees;

  callers = callers.filter((caller) => caller.signature.length);

  // Sort callers by caller.metrics.runs
  callers.sort((a, b) => b.metrics.runs.value - a.metrics.runs.value);
  callees.sort((a, b) => b.metrics.runs.value - a.metrics.runs.value);

  // Calculate total runs.value for callers
  const totalCallerRuns = callers.reduce(
    (acc, caller) => acc + caller.metrics.runs.value,
    0,
  );
  const totalCallerEndpointRuns = functionData.directFlows.reduce(
    (acc, endpoint) => acc + endpoint.runs,
    0,
  );
  const totalCalleesRuns = callees.reduce(
    (acc, callee) => acc + callee.metrics.runs.value,
    0,
  );

  // For each caller
  const { callersNodes, callerEdges } = callerToNodesAndEdges({
    callers,
    functionData,
    showTinting,
    tintColors,
    loading,
    compact,
    totalRuns: totalCallerRuns + totalCallerEndpointRuns,
    nodeHeight: NODE_HEIGHT,
    nodeWidth: options.nodeWidth,
  });

  // For each caller flow
  const { flowNodes, flowEdges } = getFlowsToNodesAndEdges({
    functionData,
    totalRuns: totalCallerRuns + totalCallerEndpointRuns,
    compact,
    nodeHeight: NODE_HEIGHT,
    nodeWidth: options.nodeWidth,
    options,
    showTinting,
    tintColors,
  });

  // Merge FLows + Callers, Sort and Cap
  const [allCallerNodes, allCallerEdges] = mergeCallersAndFlows({
    callersNodes,
    callerEdges,
    flowNodes,
    flowEdges,
    hideCappedNodes: capCallers,

    centerFunctionId: `C${functionData.function.id}`,

    additionalNodeData: {
      cta: 'toggle-callers',
      compact,
      width: options.nodeWidth,
    },

    options,
  });

  // For each callee
  const handles: number[] = [];
  let calleesNodes: Node<NodeData>[] = [];
  const calleesEdges: Edge<EdgeData>[] = [];
  for (const callee of callees) {
    const height = (callee.metrics.runs.value / totalCalleesRuns) * NODE_HEIGHT;
    const calleeId = `O${callee.id}`;
    const isTinted =
      !!showTinting && isCalleeTinted(functionData, callee, showTinting);

    const [funcName, funcParams] = splitSignature(
      callee.name,
      callee.signature,
    );

    const calleeTitle = (
      <NodeTitleWithIconContainer
        title={compact ? callee.signature : funcName}
        icon={<ModuleIcon thirdPartyModule={callee.thirdPartyModule} withTooltip={false} />}
      />
    )

    calleesNodes.push({
      id: `${calleeId}`,
      data: {
        title: calleeTitle,
        label: funcParams,
        loading,
        tooltip: callee.signature,
        handles: [height],
        compact,
        width: options.nodeWidth,
        tintColor: isTinted ? tintColors.error.color : undefined,
        notClickable: !!callee.thirdPartyModule,
        function: {
          id: callee.id,
          name: callee.name,
          signature: callee.signature,
          filePath: callee.filePath,
          type: 'CALLEE',
          identity: callee.identity,
          thirdPartyModule: callee.thirdPartyModule,
        },
      },
      type: 'outputFunction',
      ...nodeDefaults,
    });

    calleesEdges.push({
      id: `C${functionData.function.id}-edge-${calleeId}`,
      source: `C${functionData.function.id}`,
      target: `${calleeId}`,
      data: {
        color: isTinted ? tintColors.error.color : undefined,
        highlightColor: isTinted ? tintColors.error.highlightColor : undefined,
        height,

        showInspector: true,
        inspectorData: {
          type: 'CALLEE',
          payload: callee,
        },
      },
    });

    handles.push(height);
  }

  calleesNodes.sort((a, b) => {
    // Sort callees by tinted (erroneous) first, then by edgeStrokeWidth
    const isAErroneous = !!a.data.tintColor;
    const isBErroneous = !!b.data.tintColor;
    if (isAErroneous && !isBErroneous) return -1;
    if (!isAErroneous && isBErroneous) return 1;
    const aStroke = a.data.edgeStrokeWidth || 0;
    const bStroke = b.data.edgeStrokeWidth || 0;
    return bStroke - aStroke;
  });

  // cap the number of callees, if there are too many. put the rest in a single node
  const otherCallees = calleesNodes.slice(options.nodeCap);
  if (capCallees) {
    calleesNodes = calleesNodes.slice(0, options.nodeCap);
  }

  if (otherCallees.length) {
    const otherCalleeId = `O${functionData.function.id}-other-callees`;

    const otherCalleesNode: Node<NodeData> = {
      id: otherCalleeId,
      data: {
        title: compact
          ? capCallees
            ? `${otherCallees.length} more`
            : 'less'
          : capCallees
            ? `Show ${otherCallees.length} more`
            : 'Show less',
        cta: 'toggle-callees',
        compact,
        width: options.nodeWidth,
        handles: [0],
      },
      type: 'outputFunction',
      ...nodeDefaults,
    };

    calleesNodes.push(otherCalleesNode);

    // Only show line when 'show more' is visible (capCallees)
    if (capCallees) {
      const isOneOfOtherIsTinted =
        !!showTinting &&
        otherCallees.some((callee) => callee.data.tintColor !== undefined);

      // Push false edge to mark the end of the callees
      calleesEdges.push({
        id: `C${functionData.function.id}-edge-showMore`,
        source: `C${functionData.function.id}`,
        target: otherCalleeId,
        data: {
          color: isOneOfOtherIsTinted ? tintColors.error.color : undefined,
          highlightColor: isOneOfOtherIsTinted
            ? tintColors.error.highlightColor
            : undefined,
        },
      });
    }
  }

  const [funcName, funcParams] = splitSignature(
    functionData.function.name,
    functionData.function.signature,
  );

  // Center node
  const centerNodeTitle = compact
    ? `You are here ${loading ? ' loading' : ''}`
    : funcName;
  const centerNodeWidth = compact ? 'fit-content' : '320px';
  const funcNode: Node<NodeData> = {
    id: `C${functionData.function.id}`,
    data: {
      title: centerNodeTitle,
      label: funcParams,
      tooltip: functionData.function.signature,
      loading,
      handles,
      compact,
      alignToTop: !!options?.alignCenterNodeToTop,
      width: centerNodeWidth,
      function: {
        id: functionData.function.id,
        name: functionData.function.name,
        signature: functionData.function.signature,
        filePath: functionData.function.filePath,
        type: 'FUNCTION',
        // identity will be passed to the navigation service through onNodeClick defined in functionInfo

        errors: functionData.errors,
        metrics: functionData.function.metrics,
        thirdPartyModule: functionData.function.thirdPartyModule,
      },
    },
    type: 'centerFunction',
    ...nodeDefaults,
  };

  const nodes = [funcNode, ...calleesNodes, ...allCallerNodes];
  const edges = [...allCallerEdges, ...calleesEdges];
  return [nodes, edges];
}

export function splitSignature(
  functionName: string,
  functionSignature: string,
): [string, string] {
  if (!functionSignature.startsWith(functionName)) {
    return ['', functionSignature];
  }
  const nameLength = functionName.length;
  const rest = functionSignature.slice(nameLength);
  return [functionName, rest];
}

export function isCallerTinted(
  functionData: GetFunctionResponse,
  callerErrorRate: number,
  config: NodeErrorConfig,
): boolean {
  if (
    functionData.function.metrics.runs.value <= config.minimumInvocationsDay
  ) {
    return false;
  }
  if (callerErrorRate > config.callers.minimumFunctionErrorRate) {
    return true;
  }
  return false;
}

export function isCalleeTinted(
  functionData: GetFunctionResponse,
  callee: Callees[0],
  config: NodeErrorConfig,
): boolean {
  if (
    functionData.function.metrics.runs.value <= config.minimumInvocationsDay
  ) {
    return false;
  }
  if (
    callee.metrics.errorRate.value > config.callees.minimumFunctionErrorRate
  ) {
    return true;
  }

  return false;
}

function callerToNodesAndEdges({
  callers,
  functionData,
  showTinting,
  tintColors,
  loading,
  compact,
  totalRuns,
  nodeHeight,
  nodeWidth,
}: {
  callers: Callers;
  functionData: GetFunctionResponse;
  showTinting?: NodeErrorConfig;
  tintColors: TintColors;
  loading?: boolean;
  compact: boolean;
  totalRuns: number;
  nodeHeight: number;
  nodeWidth?: string;
}): { callersNodes: Node<NodeData>[]; callerEdges: Edge<EdgeData>[] } {
  const callersNodes: Node<NodeData>[] = [];
  const callerEdges: Edge<EdgeData>[] = [];

  for (const caller of callers) {
    const edgeStrokeWidth =
      (caller.metrics.runs.value / totalRuns) * nodeHeight;

    const callerId = `I${caller.id}`;
    const [funcName, funcParams] = splitSignature(
      caller.name,
      caller.signature,
    );

    const isTinted =
      !!showTinting &&
      isCallerTinted(functionData, caller.metrics.errorRate.value, showTinting);

    const callerTitle = (
      <NodeTitleWithIconContainer
        title={compact ? caller.signature : funcName}
        icon={<ModuleIcon thirdPartyModule={caller.thirdPartyModule} withTooltip={false} />}
      />
    );

    callersNodes.push({
      id: callerId,
      data: {
        title: callerTitle,
        label: funcParams,
        tooltip: caller.signature,
        loading,
        compact,
        edgeStrokeWidth: edgeStrokeWidth,
        width: nodeWidth,
        tintColor: isTinted ? tintColors.error.color : undefined,
        notClickable: !!caller.thirdPartyModule,
        function: {
          id: caller.id,
          name: caller.name,
          signature: caller.signature,
          filePath: caller.filePath,
          type: 'CALLER',
          identity: caller.identity,
          thirdPartyModule: caller.thirdPartyModule,
        },
      },
      type: 'inputFunction',
      ...nodeDefaults,
    });

    callerEdges.push({
      id: `${callerId}-edge-C${functionData.function.id}`,
      source: `${callerId}`,
      target: `C${functionData.function.id}`,
      data: {
        height: edgeStrokeWidth,
        color: isTinted ? tintColors.error.color : undefined,
        highlightColor: isTinted ? tintColors.error.highlightColor : undefined,

        showInspector: true,
        inspectorData: {
          type: 'CALLER',
          payload: caller,
        },
      },
    });
  }
  return { callersNodes, callerEdges };
}
function getFlowsToNodesAndEdges({
  functionData,
  totalRuns,
  compact,
  nodeHeight,
  nodeWidth,
  options,
  showTinting,
  tintColors,
}: {
  functionData: GetFunctionResponse;
  totalRuns: number;

  compact: boolean;

  nodeHeight: number;
  nodeWidth?: string;

  options?: NodeGenerationOptions;

  showTinting?: NodeErrorConfig;
  tintColors: TintColors;
}): { flowNodes: Node<NodeData>[]; flowEdges: Edge<EdgeData>[] } {
  const flowNodes: Node<NodeData>[] = [];
  const flowEdges: Edge<EdgeData>[] = [];

  const numGroupsPerTopic = functionData.directFlows.reduce<
    Record<string, number>
  >((acc, topic) => {
    if (!isKafkaTopic(topic)) {
      return acc;
    }
    acc[topic.kafka_topic_name] = acc[topic.kafka_topic_name] || 0;
    acc[topic.kafka_topic_name]++;
    return acc;
  }, {});

  function isMultiGroupTopic(topic: FlowByFunction) {
    return isKafkaTopic(topic) && numGroupsPerTopic[topic.kafka_topic_name] > 1;
  }

  for (const flow of functionData.directFlows) {
    const isTinted =
      !!showTinting &&
      isCallerTinted(functionData, flow.error_rate ?? 0, showTinting);
    if (isKafkaTopic(flow)) {
      const nodeId = `I${flow.kafka_topic_name}_${flow.kafka_topic_group_id}_${flow.id}_${flow.service.id}`;
      const edgeStrokeWidth = (flow.runs / totalRuns) * nodeHeight;
      flowNodes.push({
        id: nodeId,
        data: {
          title: (
            <TopicNodeBody
              topic={flow}
              isMultiGroupTopic={isMultiGroupTopic(flow)}
            />
          ),
          tooltip: `${flow.kafka_topic_name}${
            isMultiGroupTopic(flow)
              ? ` (group ${flow.kafka_topic_group_id})`
              : ''
          } ${flow.service.name}`,
          compact,
          edgeStrokeWidth: edgeStrokeWidth,
          width: nodeWidth,
          notClickable: options?.clickableTopics !== true,
          tintColor: isTinted ? tintColors.error.color : undefined,
          function: {
            id: flow.id,
            name: flow.kafka_topic_name,
            signature: `${flow.kafka_topic_name}${
              isMultiGroupTopic(flow)
                ? ` (group ${flow.kafka_topic_group_id})`
                : ''
            }`,
            filePath: '',
            type: 'FLOW',
            flowType: 'QUEUE',
            serviceId: flow.service.id,
          },
        },
        type: 'inputFunction',
        ...nodeDefaults,
      });
      flowEdges.push({
        id: `${nodeId}-edge-C${functionData.function.id}`,
        source: `${nodeId}`,
        target: `C${functionData.function.id}`,
        data: {
          showInspector: true,
          inspectorData: {
            type: 'FLOW',
            payload: flow,
          },
          height: edgeStrokeWidth,
          color: isTinted ? tintColors.error.color : undefined,
          highlightColor: isTinted
            ? tintColors.error.highlightColor
            : undefined,
        },
      });
    } else if (isEndpoint(flow)) {
      const nodeId = `I${flow.id}_${flow.service.id}`;
      const edgeStrokeWidth = (flow.runs / totalRuns) * nodeHeight;
      const displayMethod = getDisplayMethod(flow.methods);
      flowNodes.push({
        id: nodeId,
        data: {
          title: (
            <EndpointNodeBody endpoint={flow} methodsDisplay={displayMethod} />
          ),
          tooltip: (
            <EndpointTooltipContent
              methods={flow.methods}
              path={flow.path}
              service={(isHttpEndpoint(flow) && flow.service.name) || ''}
            />
          ),
          compact,
          edgeStrokeWidth: edgeStrokeWidth,
          width: nodeWidth,
          notClickable: options?.clickableEndpoints !== true,
          tintColor: isTinted ? tintColors.error.color : undefined,
          function: {
            id: flow.id,
            name: flow.path,
            signature: flow.path,
            filePath: '',
            type: 'FLOW',
            flowType: 'ENDPOINT',
          },
        },
        type: 'inputFunction',
        ...nodeDefaults,
      });

      flowEdges.push({
        id: `${nodeId}-edge-C${functionData.function.id}`,
        source: `${nodeId}`,
        target: `C${functionData.function.id}`,
        data: {
          showInspector: true,
          inspectorData: {
            type: 'FLOW',
            payload: flow,
          },
          height: edgeStrokeWidth,
          color: isTinted ? tintColors.error.color : undefined,
          highlightColor: isTinted
            ? tintColors.error.highlightColor
            : undefined,
        },
      });
    } else if (isSqsQueue(flow)) {
      const nodeId = `I${flow.sqs_queue_name}_${flow.id}_${flow.service.id}`;
      const edgeStrokeWidth = (flow.runs / totalRuns) * nodeHeight;
      flowNodes.push({
        id: nodeId,
        data: {
          title: <SQSQueueNodeBody queue={flow} />,
          tooltip: `${flow.sqs_queue_name} ${flow.service.name}`,
          compact,
          edgeStrokeWidth: edgeStrokeWidth,
          width: nodeWidth,
          notClickable: true,
          tintColor: isTinted ? tintColors.error.color : undefined,
          function: {
            id: flow.id,
            name: flow.sqs_queue_name,
            signature: `${flow.sqs_queue_name}`,
            filePath: '',
            type: 'FLOW',
          },
        },
        type: 'inputFunction',
        ...nodeDefaults,
      });
      flowEdges.push({
        id: `${nodeId}-edge-C${functionData.function.id}`,
        source: `${nodeId}`,
        target: `C${functionData.function.id}`,
        data: {
          showInspector: true,
          inspectorData: {
            type: 'FLOW',
            payload: flow,
          },
          height: edgeStrokeWidth,
          color: isTinted ? tintColors.error.color : undefined,
          highlightColor: isTinted
            ? tintColors.error.highlightColor
            : undefined,
        },
      });
    }
  }
  return { flowNodes, flowEdges };
}

function mergeCallersAndFlows({
  callersNodes,
  callerEdges,
  flowNodes,
  flowEdges,

  hideCappedNodes,
  centerFunctionId,
  additionalNodeData,

  options,
}: {
  callersNodes: Node<NodeData>[];
  callerEdges: Edge<EdgeData>[];
  flowNodes: Node<NodeData>[];
  flowEdges: Edge<EdgeData>[];
  hideCappedNodes: boolean;
  centerFunctionId: string;
  additionalNodeData?: Partial<NodeData>;
  options?: NodeGenerationOptions;
}): [Node<NodeData>[], Edge<EdgeData>[]] {
  const nodeCap = options?.nodeCap || DEFAULT_NODE_CAP;
  let nodes = [...callersNodes, ...flowNodes];
  const edges = [...callerEdges, ...flowEdges];
  const showToggleButton = nodes.length > nodeCap;

  let hiddenNodes: Node<NodeData>[] = [];
  // let hiddenEdges = [];

  // Sort nodes + edges by erroneous functions first, then by edge.data.height
  nodes.sort((a, b) => {
    const isAErroneous = !!a.data.tintColor;
    const isBErroneous = !!b.data.tintColor;
    if (isAErroneous && !isBErroneous) return -1;
    if (!isAErroneous && isBErroneous) return 1;

    const aStroke = a.data.edgeStrokeWidth || 0;
    const bStroke = b.data.edgeStrokeWidth || 0;
    return bStroke - aStroke;
  });

  if (hideCappedNodes) {
    hiddenNodes = nodes.slice(nodeCap);
    nodes = nodes.slice(0, nodeCap);
  }

  // If there are actual leftovers
  if (showToggleButton) {
    const toggleNodeId = `I${centerFunctionId}-other-callers`;
    const title = hideCappedNodes
      ? `Show ${hiddenNodes.length} more`
      : 'Show less';

    const toggleNode: Node<NodeData> = {
      ...nodeDefaults,
      id: toggleNodeId,
      type: 'inputFunction',
      data: {
        title,
        ...additionalNodeData,
      },
    };
    nodes.push(toggleNode);

    // Edge is visible only in `capNodes` mode
    if (hideCappedNodes) {
      const hiddenTintedNode = hiddenNodes.find((node) => node.data.tintColor);

      edges.push({
        id: `toggleHiddenCallers-edge-C${centerFunctionId}`,
        source: toggleNodeId,
        target: centerFunctionId,
        data: {
          color: hiddenTintedNode?.data.tintColor,
          highlightColor: hiddenTintedNode?.data.tintColor,
        },
      });
    }
  }

  return [nodes, edges];
}
